<script setup lang="ts">
import {
  nextTick,
  onBeforeUnmount,
  onMounted,
  onUpdated,
  ref,
  useId,
  useTemplateRef,
  watch,
} from "vue";

import UiIcon from "@/components/UiIcon.vue";
import { anchorOutOfView, placeAnchored } from "@/utils/anchorPosition";

/**
 * 설명 토글팁(74차 UXV74-04) — ⓘ 버튼을 누르면 짧은 설명 판이 열린다. 상태 · 잠김 · 색 기준 설명이 마우스 `title` 에만 있어
 * 터치 · 키보드 사용자가 볼 수 없던 것(배지 36 · 표 머리 6 …)을 옮기는 자리다.
 * - 토글팁이지 툴팁이 아니다: 마우스를 올려도 열리지 않고, 누름(클릭 · Enter · Space)으로만 연다 — 터치 · 키보드 · 마우스가 같은 길.
 * - 판은 Popover API(`popover="auto"`)의 top layer 에 뜬다: 바깥을 누르거나 Esc 면 닫히고(Esc 는 포커스를 버튼에 둔다), 버튼은
 *   판의 여는 버튼(`popoverTargetElement`)이라 열린 판을 다시 누르면 닫힌다. 열린 판의 Esc 는 판만 닫는다(모달 안에서 모달까지 닫지 않는다). Teleport 하지 않으므로 `aria-modal` 대화상자(AppModal · 모달 SidePanel) 안에서도
 *   판 DOM 이 대화상자 안에 남아 낭독 · 포커스 가둠에서 빠지지 않는다(MenuButton 의 "대화상자 안으로 Teleport" 우회가 필요 없다).
 * - 열릴 때 판 글을 `role="status"` 로 한 번 읽는다(토글팁 패턴 — 판이 보인 뒤 숨은 상태 영역에 같은 글을 넣어야 낭독기가 알아챈다).
 * - 위치는 `placeAnchored`(MenuButton 과 같은 계산) — 버튼 아래, 모자라면 위. 열린 동안 스크롤 · 창 크기가 바뀌면 다시 붙인다.
 * - 판에는 **짧은 글**(≤3문장)만 둔다. 버튼 · 링크를 넣지 않는다 — 해야 할 동작은 화면 본문에 둔다.
 * - 잠긴 입력칸의 이유는 이 부품이 아니라 칸 아래 보이는 줄(`ui-btn-reason` 모양 + `aria-describedby`)로 둔다(UXB-07).
 * - Popover API 가 없는 브라우저(2024 이전)는 같은 판을 `hidden` 으로 열고 닫고, 바깥 누름 · Esc 를 직접 듣는다.
 * - 판은 top layer 라 z-index 를 무시하고 토스트보다 위에 뜬다 — 짧은 글이고 바깥을 누르면 닫히므로 그대로 둔다(ux-viz §6 위험).
 * - 82차 리뷰 R82-DS-M1 · L5: 앞 글이 **공백으로 끝나면**(호출부 대부분) 그 공백이 줄바꿈 기회라 위 묶음만으로는 ⓘ 가 여전히 다음 줄 맨 앞에 홀로 섰다(공백은 부모 글의 줄바꿈이라 자식이 막을 수 없다 — CSS Text 3).
 *   그래서 ⓘ 뒤에 이어지는 글이 **없을 때**(문장 끝 ⓘ — 거의 전부)는 「걸림 모드」 `is-hang`: 루트 폭 0(안쪽 여백 없음)이라 줄 끝에 항상 들어가고, 버튼이 줄 끝 오른쪽으로 최대 24px 걸쳐 나온다(바깥 여백 · 카드 안쪽 여백으로).
 *   뒤에 글이 이어지면(문장 중간 ⓘ) · 부모가 `overflow` 를 자르면(걸친 버튼이 잘린다) 위 안쪽 여백 모드로 둔다.
 *   83차 M15(UXC83-01 = UXV83-04): **표 머리 `th` 안**에서도 안쪽 여백 모드다 — 걸친 버튼이 옆 칸으로 나가는데 옆 sticky `th` 가 그 위를 칠해 버튼이 「(」 반달로 보이고 눌리지 않았다. 안쪽 여백 모드는
 *   버튼이 앞 글 뒤 24px 자리(칸 안쪽)에 서므로 칸 오른쪽 선을 넘지 않는다. 화면 scoped `padding-inline-end` 로 여백을 따로 주지 않는다. 모드는 마운트 · 갱신 · 부모 크기 변화(ResizeObserver — 미디어 쿼리로 부모 `display` 가 바뀌는 머리 줄)마다 다시 정한다.
 * - 82차 a-C7(UXA82-20): 글줄 안(부모가 flex · grid 가 아닌 곳)에 놓이면 ⓘ 는 **직전 단어와 한 덩어리**다 — 원자 인라인(inline-flex) 앞에는 줄바꿈 기회가 늘 있어(CSS Text 3) 설명 문장 끝에서 ⓘ 만 다음 줄
 *   맨 앞에 홀로 섰다. 글줄 모드는 루트를 내용 없는 `inline` 상자(`position: relative` + 앞쪽 안쪽 여백 24)로 두고 버튼은 그 여백 자리에 절대 배치한다 — 글 낱말 + 빈 인라인 상자 사이에는 줄바꿈 기회가 없어
 *   낱말째 줄을 넘는다. 부모가 flex · grid 면(머리 줄 · 표 머리 등 자기 칸을 가진 자리) 지금 모양 그대로다(마운트 때 부모 `display` 로 판정). 호출부 마크업 변경 없음.
 * - 75차(UXS75-05 ①): 낭독 영역은 판 **밖**(루트 안 늘 그려진 숨은 글)에 둔다. 판 안에 두면 판이 닫힌 동안 `display: none` 이라 열릴 때
 *   영역이 새로 생긴 뒤 글이 들어가 VoiceOver · JAWS 가 자주 놓쳤다. 열릴 때 글을 넣고 닫히면 비운다.
 * - 75차(UXS75-05 ②): 스크롤로 버튼이 띠(상단바 아래 ~ 보이는 화면 바닥) 밖에 나가면 판을 닫는다(`anchorOutOfView`). 설명 판이
 *   대상 없이 가장자리에 붙어 떠 있지 않게 — 메뉴(MenuButton)는 그대로 화면 안에 남는다.
 */
const props = withDefaults(
  defineProps<{
    /** 설명 대상 이름 — 버튼 접근 이름 「설명: {label}」 */
    label: string;
    /** 판 글(기본 슬롯이 우선) */
    text?: string;
    /** 판을 버튼 왼쪽 끝(start) · 오른쪽 끝(end)에 맞춘다 */
    placement?: "bottom-start" | "bottom-end";
  }>(),
  { text: undefined, placement: "bottom-start" },
);

defineSlots<{
  /** 판 글(text 대신 — 굵은 값 등 인라인 마크업이 필요할 때) */
  default?: () => unknown;
}>();

/** 버튼과 판 사이 · 화면 가장자리 여백(px) */
const GAP = 6;
const EDGE = 8;
/** 판이 열린 뒤 글을 넣기까지(ms) — 토글팁 낭독 패턴 */
const ANNOUNCE_DELAY_MS = 50;

const panelId = useId();
const root = useTemplateRef<HTMLElement>("root");
const trigger = useTemplateRef<HTMLButtonElement>("trigger");
const panel = useTemplateRef<HTMLElement>("panel");
const open = ref(false);
/** 낭독 영역 글 — 판이 보인 뒤에 넣어야 읽는다(보이는 글은 따로 늘 그려 자리 계산이 글 크기로 된다) */
const announcement = ref("");
const textEl = useTemplateRef<HTMLElement>("textEl");
const positioned = ref(false);
const panelStyle = ref<Record<string, string>>({});
/** 부모가 글줄이다(flex · grid 가 아님) — 마운트 전에는 지금 모양(inline-flex)으로 그리고 마운트 직후 판정한다 */
const inText = ref(false);
/** 문장 끝 ⓘ — 폭 0 으로 줄 끝에 걸친다(앞 글이 공백으로 끝나도 줄 머리에 홀로 서지 않는다) */
const hang = ref(false);
let parentObserver: ResizeObserver | undefined;

/** 이 ⓘ 뒤로 이어지는 글(공백 · 주석 제외)이 없다 */
function nothingFollows(el: HTMLElement): boolean {
  for (let node = el.nextSibling; node; node = node.nextSibling) {
    if (node.nodeType === Node.ELEMENT_NODE) return false;
    if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim())
      return false;
  }
  return true;
}

/** 부모 `display` · `overflow` 와 뒤 글로 모드를 다시 정한다 */
function syncMode(): void {
  const el = root.value;
  const parent = el?.parentElement;
  if (!el || !parent) return;
  const style = getComputedStyle(parent);
  inText.value = !/\b(?:inline-)?(?:flex|grid)\b|^contents$/u.test(
    style.display,
  );
  hang.value =
    inText.value &&
    !el.closest("th, [role='columnheader']") &&
    nothingFollows(el) &&
    style.overflowX === "visible" &&
    style.textOverflow !== "ellipsis";
}
const supportsPopover =
  typeof HTMLElement !== "undefined" &&
  typeof HTMLElement.prototype.togglePopover === "function";
let frame = 0;

function place(): void {
  const button = trigger.value;
  const el = panel.value;
  if (!button || !el) return;
  if (anchorOutOfView(button)) {
    close();
    return;
  }
  const { top, left } = placeAnchored(button, el, {
    placement: props.placement,
    gap: GAP,
    edge: EDGE,
  });
  panelStyle.value = { top: `${top}px`, left: `${left}px` };
  positioned.value = true;
}

function schedulePlace(): void {
  cancelAnimationFrame(frame);
  frame = requestAnimationFrame(place);
}

function onOutsidePointer(event: PointerEvent): void {
  const target = event.target;
  if (!(target instanceof Node)) return;
  if (trigger.value?.contains(target) || panel.value?.contains(target)) return;
  open.value = false;
}

/**
 * 열린 동안의 Esc — 판만 닫고 아래 층(AppModal · SidePanel · AI 도우미의 document ESC)까지 가지 않게 가장 먼저(window 캡처) 멈춘다.
 * 기본 동작은 막지 않으므로 Popover API 의 닫기는 그대로 일어난다. Popover 가 없으면 직접 닫고 버튼으로 포커스를 둔다.
 */
function onEscape(event: KeyboardEvent): void {
  if (event.key !== "Escape" || event.isComposing) return;
  event.stopPropagation();
  if (supportsPopover) return;
  open.value = false;
  trigger.value?.focus();
}

function listen(on: boolean): void {
  if (on) {
    window.addEventListener("scroll", schedulePlace, {
      capture: true,
      passive: true,
    });
    window.addEventListener("resize", schedulePlace, { passive: true });
    window.addEventListener("keydown", onEscape, { capture: true });
    if (!supportsPopover)
      document.addEventListener("pointerdown", onOutsidePointer, {
        capture: true,
      });
    return;
  }
  window.removeEventListener("scroll", schedulePlace, { capture: true });
  window.removeEventListener("resize", schedulePlace);
  window.removeEventListener("keydown", onEscape, { capture: true });
  document.removeEventListener("pointerdown", onOutsidePointer, {
    capture: true,
  });
}

watch(open, async (isOpen) => {
  listen(isOpen);
  if (!isOpen) {
    announcement.value = "";
    positioned.value = false;
    return;
  }
  await nextTick();
  place();
  // 판이 보인 뒤 잠깐 있다가 낭독 영역에 글을 넣는다 — 영역과 글이 같은 틱에 생기면 낭독기가 놓친다.
  await new Promise((resolve) => setTimeout(resolve, ANNOUNCE_DELAY_MS));
  if (!open.value) return;
  announcement.value = textEl.value?.textContent?.trim() ?? "";
});

function close(): void {
  if (supportsPopover && panel.value?.matches(":popover-open"))
    panel.value.hidePopover();
  open.value = false;
}

/** Popover API 의 열림 · 닫힘(버튼 · Esc · 바깥 누름 모두 이 이벤트로 온다) */
function onToggle(event: Event): void {
  open.value = (event as ToggleEvent).newState === "open";
}

/** Popover API 가 없을 때만 직접 연다(있으면 여는 버튼 연결 — `popoverTargetElement` — 이 한다) */
function onTriggerClick(): void {
  if (!supportsPopover) open.value = !open.value;
}

// 버튼 ↔ 판 연결은 id 참조(`popovertarget` 속성) 대신 요소로 잇는다 — 앱이 둘(공개 셸 미리보기 등)이면 useId 가 겹칠 수 있다.
// 연결된 버튼은 Popover API 가 "여는 버튼" 으로 알아 열린 판을 다시 누르면 닫는다(바깥 누름 닫기에서 빠진다).
onMounted(() => {
  if (supportsPopover && trigger.value && panel.value)
    trigger.value.popoverTargetElement = panel.value;
  syncMode();
  const parent = root.value?.parentElement;
  if (parent && typeof ResizeObserver !== "undefined") {
    parentObserver = new ResizeObserver(syncMode);
    parentObserver.observe(parent);
  }
});
// 뒤 글이 바뀌어 다시 그려질 때(조건부 글 · 문구 교체)도 모드를 다시 본다.
onUpdated(syncMode);

onBeforeUnmount(() => {
  parentObserver?.disconnect();
  listen(false);
  cancelAnimationFrame(frame);
});
</script>

<template>
  <span
    ref="root"
    class="ui-infotip"
    :class="{ 'is-in-text': inText, 'is-hang': hang }"
  >
    <button
      ref="trigger"
      type="button"
      class="ui-infotip-trigger"
      :aria-label="`설명: ${label}`"
      :aria-expanded="open ? 'true' : 'false'"
      :aria-controls="panelId"
      @click="onTriggerClick"
    >
      <UiIcon name="info" :size="16" />
    </button>
    <span
      :id="panelId"
      ref="panel"
      class="ui-infotip-panel"
      :class="{ 'is-positioned': positioned }"
      :popover="supportsPopover ? 'auto' : undefined"
      :hidden="!supportsPopover && !open"
      :style="panelStyle"
      @toggle="onToggle"
    >
      <!-- 보이는 글은 낭독에서 빼고(두 번 읽지 않게) 같은 글을 숨은 상태 영역이 한 번 읽는다 -->
      <span ref="textEl" class="ui-infotip-text" aria-hidden="true"
        ><slot>{{ text }}</slot></span
      >
    </span>
    <span class="ui-sr-only" role="status">{{ announcement }}</span>
  </span>
</template>

<style scoped>
.ui-infotip {
  display: inline-flex;
  flex: none;
  vertical-align: middle;
}

/*
 * 글줄 모드(82차 a-C7) — 내용 없는 인라인 상자 + 앞쪽 안쪽 여백 24 가 자리, 버튼은 그 자리에 절대 배치. 낱말 뒤에 줄바꿈 기회가 없어 ⓘ 가 홀로 줄 머리에 서지 않는다.
 * 버튼 높이 24 는 글줄 가운데(상자 높이의 50%)에 맞춘다.
 */
.ui-infotip.is-in-text {
  position: relative;
  display: inline;
  padding-inline-start: 24px;
  vertical-align: baseline;
}

/* 걸림 모드 — 폭 0: 줄 끝에 늘 들어가 줄 머리에 홀로 서지 않는다. 버튼은 같은 자리(앞 글 끝)에서 오른쪽으로 걸친다. */
.ui-infotip.is-in-text.is-hang {
  padding-inline-start: 0;
}

.ui-infotip.is-in-text > .ui-infotip-trigger {
  position: absolute;
  top: 50%;
  inset-inline-start: 0;
  margin: -12px 0 0;
}

/* 아이콘 16 · 누름 자리 24(WCAG 2.5.8) — 글줄 높이를 늘리지 않게 위아래를 음수 여백으로 걷는다 */
.ui-infotip-trigger {
  display: inline-grid;
  place-items: center;
  width: 24px;
  height: 24px;
  margin: -4px 0;
  padding: 0;
  border: 0;
  border-radius: var(--ui-radius-pill);
  background: none;
  color: var(--ui-text-3);
  cursor: pointer;
  transition: color var(--ui-dur-fast) var(--ui-ease);
}

.ui-infotip-trigger:hover,
.ui-infotip-trigger[aria-expanded="true"] {
  color: var(--ui-blue-700);
}

.ui-infotip-trigger:focus-visible {
  outline: var(--ui-focus-outline);
  outline-offset: 1px;
}

/*
 * 판 — UA 의 popover 기본(가운데 · inset 0)을 걷고 버튼 좌표(fixed)에 둔다. 자리를 잡기 전 한 프레임은 숨긴다(가운데에서 튀지 않게).
 * Popover 가 없는 브라우저는 `hidden` 이 열고 닫으므로 같은 fixed 판에 층만 준다.
 */
.ui-infotip-panel {
  position: fixed;
  inset: auto;
  z-index: var(--ui-z-notice);
  width: max-content;
  max-width: min(320px, calc(100vw - 16px));
  margin: 0;
  padding: 10px 12px;
  overflow: visible;
  border: 1px solid var(--ui-line-strong);
  border-radius: var(--ui-radius-sm);
  background: var(--ui-surface);
  box-shadow: var(--ui-shadow-3);
  color: var(--ui-text-sub);
  font-size: var(--ui-fs-sm);
  font-weight: 500;
  line-height: 1.55;
  text-align: left;
  white-space: normal;
  word-break: keep-all;
  overflow-wrap: break-word;
  visibility: hidden;
}

.ui-infotip-panel[hidden] {
  display: none;
}

.ui-infotip-panel.is-positioned {
  visibility: visible;
}

.ui-infotip-text {
  display: block;
}

.ui-infotip-text :deep(b),
.ui-infotip-text :deep(strong) {
  color: var(--ui-text);
  font-weight: 700;
}
</style>
